// =============================================================================
// DroppableResourceRow - Riga risorsa con drop zone e appuntamenti
// =============================================================================

import { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '../../../lib/utils';
import { ROW_HEIGHT } from '../utils/grid.constants';
import { DraggableAppointmentBlock } from './DraggableAppointmentBlock';
import { DroppableCell } from './DroppableCell';
import { ExceptionBand } from './ExceptionBand';
import type { ResourceRowProps } from '../types/scheduler.types';
import { useTenantTimeZone } from '../../../store/tenantStore';
import { civilDayOf, wallClockToUtc } from '../../../lib/tenantTime';

export function DroppableResourceRow({ resource, appointments, exceptions = [], hours, dayStart, services, showBufferZones, gridStartHour, gridEndHour, totalGridMinutes, onSlotClick, onAppointmentClick }: ResourceRowProps) {
  const timeZone = useTenantTimeZone();
  const { setNodeRef, isOver } = useDroppable({
    id: `resource-${resource.id}`,
    data: { resourceId: resource.id, resourceName: resource.name },
  });
  
  const resourceAppointments = useMemo(() => {
    return appointments.filter((apt) =>
      apt.allocations?.some((alloc) => alloc.resourceId === resource.id)
    );
  }, [appointments, resource.id]);

  const handleCellClick = (hour: number, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const cellWidth = rect.width;
    const minuteOffset = Math.floor((clickX / cellWidth) * 60);
    // Il punto cliccato e' un orario di parete dell'organizzazione.
    const start = wallClockToUtc(civilDayOf(dayStart), hour * 60 + minuteOffset, timeZone);
    onSlotClick(start, resource.id, resource.name);
  };

  return (
    <div 
      className={cn(
        'flex border-b border-gray-100 transition-colors duration-200',
        isOver ? 'bg-blue-100/50' : 'hover:bg-gray-50/50'
      )}
      style={{ height: `${ROW_HEIGHT}px` }}
    >
      {/* Colonna nome risorsa */}
      <div className="w-48 flex-shrink-0 p-2 border-r border-gray-200 flex items-start gap-2">
        <div
          className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
          style={{ backgroundColor: resource.color || '#6B7280' }}
        />
        <div>
          <div className="font-medium text-sm text-gray-900 truncate">{resource.name}</div>
          <div className="text-xs text-gray-500">{resource.resourceType?.name}</div>
        </div>
      </div>
      
      {/* Griglia temporale */}
      <div ref={setNodeRef} className="flex-1 relative">
        <div className="absolute inset-0 flex">
          {hours.map((hour) => (
            <DroppableCell
              key={hour}
              resource={resource}
              resourceId={resource.id}
              exceptions={exceptions}
              hour={hour}
              dayStart={dayStart}
              hoursCount={hours.length}
              onClick={(e) => handleCellClick(hour, e)}
            />
          ))}
        </div>

        {/* Bande eccezioni (assenze / presenze aggiuntive) per il giorno corrente */}
        {exceptions.map((exc, idx) => (
          <ExceptionBand
            key={`exc-${idx}`}
            exception={exc}
            gridStartHour={gridStartHour}
            gridEndHour={gridEndHour}
          />
        ))}

        {resourceAppointments.map((apt) => {
          const service = services.find(s => s.id === apt.serviceDefinitionId);
          return (
            <DraggableAppointmentBlock
              key={`${apt.id}-${resource.id}`}
              appointment={apt}
              resourceId={resource.id}
              onAppointmentClick={onAppointmentClick}
              bufferBeforeMinutes={service?.bufferBeforeMinutes ?? 0}
              bufferAfterMinutes={service?.bufferAfterMinutes ?? 0}
              showBufferZones={showBufferZones}
              gridStartHour={gridStartHour}
              gridEndHour={gridEndHour}
              totalGridMinutes={totalGridMinutes}
            />
          );
        })}
      </div>
    </div>
  );
}
